import api from '@/api/axiosApi'
import type {
  PaginatedSbaCalendarsResponse,
  SbaCalendarCreate,
  SbaCalendarEventDateListSimple,
  SbaCalendarEventDetail,
  SbaCalendarEventRead,
  SbaCalendarRead,
} from './clt.types'

export interface SbaCalendarEventDatesQuery {
  milestone_id: string
  dc?: string
  origin_port?: string
  is_import?: boolean
  is_rail?: boolean
  strategy_codes?: string[]
}

export interface ListSbaCalendarsParams {
  search?: string
  is_active?: boolean
  is_sba?: boolean
  skip?: number
  limit?: number
}

/**
 * Lists calendars from the unified `/calendars` endpoint. Pass `is_sba: true`
 * to restrict the result to SBA calendars (the endpoint also returns standard
 * assortment calendars when unfiltered).
 */
export async function listSbaCalendars(
  params: ListSbaCalendarsParams = {}
): Promise<PaginatedSbaCalendarsResponse> {
  const { search, is_active, is_sba, skip = 0, limit = 100 } = params
  const response = await api.get<PaginatedSbaCalendarsResponse>('/calendars', {
    params: {
      search: search || undefined,
      is_active: is_active ?? undefined,
      is_sba: is_sba ?? undefined,
      skip,
      limit,
    },
    headers: { 'Cache-Control': 'no-cache' },
  })
  return response.data
}

export async function createSbaCalendar(
  payload: SbaCalendarCreate
): Promise<SbaCalendarRead> {
  const response = await api.post<SbaCalendarRead>('/calendars', payload)
  return response.data
}

export async function getSbaCalendar(id: string): Promise<SbaCalendarRead> {
  const response = await api.get<SbaCalendarRead>(`/calendars/${id}`)
  return response.data
}

export async function listSbaCalendarEvents(
  calendarId: string
): Promise<SbaCalendarEventRead[]> {
  const response = await api.get<SbaCalendarEventRead[]>(
    `/calendars/${calendarId}/events`
  )
  return response.data
}

export async function getSbaCalendarEventDetail(
  calendarId: string,
  eventId: string
): Promise<SbaCalendarEventDetail> {
  const response = await api.get<SbaCalendarEventDetail>(
    `/calendars/${calendarId}/events/${eventId}`
  )
  return response.data
}

/**
 * Set the calendar-level default logic template. Empty string clears it.
 */
export async function setSbaCalendarTemplate(
  calendarId: string,
  templateId: string | null
): Promise<SbaCalendarRead> {
  const response = await api.put<SbaCalendarRead>(
    `/calendars/${calendarId}/template`,
    { template_id: templateId }
  )
  return response.data
}

export async function setEventCltOverride(
  calendarId: string,
  eventId: string,
  templateId: string
): Promise<SbaCalendarEventRead> {
  const response = await api.put<SbaCalendarEventRead>(
    `/calendars/${calendarId}/events/${eventId}/clt-override`,
    { template_id: templateId }
  )
  return response.data
}

export async function clearEventCltOverride(
  calendarId: string,
  eventId: string
): Promise<void> {
  await api.delete(`/calendars/${calendarId}/events/${eventId}/clt-override`)
}

/**
 * Trigger server-side recompute of materialized dates for a calendar after a
 * template / override change. Fire-and-forget from the UI's perspective.
 */
export async function recomputeSbaCalendar(calendarId: string): Promise<void> {
  await api.post(`/calendars/${calendarId}/recompute`)
}

export async function recomputeSbaCalendarEvent(
  calendarId: string,
  eventId: string
): Promise<void> {
  await api.post(`/calendars/${calendarId}/events/${eventId}/recompute`)
}

export async function deleteSbaCalendar(id: string): Promise<void> {
  await api.delete(`/calendars/${id}`)
}

/**
 * Serialize query params with repeatable keys for arrays
 * (e.g. ?strategy_codes=90&strategy_codes=91), not the axios default bracketed form.
 */
function serializeDatesQuery(params: SbaCalendarEventDatesQuery): string {
  const parts: string[] = []
  const push = (k: string, v: string | number | boolean) =>
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  push('milestone_id', params.milestone_id)
  if (params.dc) push('dc', params.dc)
  if (params.origin_port) push('origin_port', params.origin_port)
  if (params.is_import !== undefined) push('is_import', params.is_import)
  if (params.is_rail !== undefined) push('is_rail', params.is_rail)
  if (params.strategy_codes?.length) {
    params.strategy_codes.forEach(c => push('strategy_codes', c))
  }
  return parts.join('&')
}

export async function listSbaCalendarEventDates(
  calendarId: string,
  eventId: string,
  params: SbaCalendarEventDatesQuery
): Promise<SbaCalendarEventDateListSimple> {
  const response = await api.get<SbaCalendarEventDateListSimple>(
    `/calendars/${calendarId}/events/${eventId}/dates`,
    { params, paramsSerializer: () => serializeDatesQuery(params) }
  )
  return response.data
}
